import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
} from '../../automation/constants';
import {
  decodeField,
  findField,
  hasFixedOptions,
  newRule,
  operatorNeedsValue,
  valueMatchesType,
} from './conditionFields';
import {
  actionBodyHeights,
  defaultActionData,
  isConversationAction,
  isPixelAction,
  isRecordAction,
  isWaitAction,
  validateActionBody,
} from './actionCatalog';
import { isMetaAction, isMetaFixed } from './metaConversion';

export const NODE_WIDTH = 288;
export const HEADER_HEIGHT = 40;
export const FIELD_HEIGHT = 32;
export const BODY_PADDING = 12;
export const FIELD_GAP = 8;
export const BRANCH_HEIGHT = 32;
export const STAGES_BOX_HEIGHT = 96;

export const DEAL_EVENTS = [
  { key: 'deal_created', labelKey: 'AUTOMATION.FLOW.EVENTS.DEAL_CREATED' },
  { key: 'deal_moved', labelKey: 'AUTOMATION.FLOW.EVENTS.DEAL_MOVED' },
  {
    key: 'deal_created_or_moved',
    labelKey: 'AUTOMATION.FLOW.EVENTS.DEAL_CREATED_OR_MOVED',
  },
];

export const TRIGGER_EVENTS = [
  ...AUTOMATION_RULE_EVENTS.map(({ key, value }) => ({
    key,
    labelKey: `AUTOMATION.EVENTS.${value}`,
  })),
  ...DEAL_EVENTS,
];

export const isDealEvent = event => DEAL_EVENTS.some(e => e.key === event);

export const actionNeedsValue = actionKey =>
  !!AUTOMATION_ACTION_TYPES.find(({ key }) => key === actionKey)?.inputType;

const fieldHeights = node => {
  if (node.type === 'condition') {
    const rules = node.data.rules || [];
    return [
      ...rules.flatMap((_, index) => [
        ...(index ? [FIELD_HEIGHT] : []),
        FIELD_HEIGHT,
        FIELD_HEIGHT,
        FIELD_HEIGHT,
      ]),
      FIELD_HEIGHT,
    ];
  }
  if (node.type === 'action') {
    const body = actionBodyHeights(node.data, {
      field: FIELD_HEIGHT,
      box: STAGES_BOX_HEIGHT,
    });
    if (body.length) return [FIELD_HEIGHT, ...body];
    return actionNeedsValue(node.data.action)
      ? [FIELD_HEIGHT, FIELD_HEIGHT]
      : [FIELD_HEIGHT];
  }
  if (!isDealEvent(node.data.event)) return [FIELD_HEIGHT];
  const heights = [FIELD_HEIGHT, FIELD_HEIGHT, FIELD_HEIGHT];
  if (node.data.any_stage === false) heights.push(STAGES_BOX_HEIGHT);
  return heights;
};

export const nodeHeight = node => {
  const heights = fieldHeights(node);
  const body =
    BODY_PADDING * 2 +
    heights.reduce((sum, h) => sum + h, 0) +
    (heights.length - 1) * FIELD_GAP;
  const branches = node.type === 'condition' ? BRANCH_HEIGHT * 2 : 0;
  return HEADER_HEIGHT + body + branches;
};

// Saídas do nó: "out" (gatilho/ação) ou "yes"/"no" (condição), com a
// posição vertical relativa ao topo do nó.
export const nodeOutputs = node => {
  const height = nodeHeight(node);
  if (node.type === 'condition') {
    return [
      { handle: 'yes', y: height - BRANCH_HEIGHT * 1.5 },
      { handle: 'no', y: height - BRANCH_HEIGHT / 2 },
    ];
  }
  return [{ handle: 'out', y: HEADER_HEIGHT / 2 }];
};

export const edgePath = (from, to) => {
  const dx = Math.max(40, Math.abs(to.x - from.x) / 2);
  return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
};

export const createNode = (type, x, y) => {
  const data = {
    trigger: { event: '', pipeline_id: '', any_stage: true, stage_ids: [] },
    condition: { rules: [newRule()] },
    action: defaultActionData(),
  }[type];
  return {
    id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type,
    x,
    y,
    data,
  };
};

export const collectDescendants = (nodeId, edges) => {
  const ids = [nodeId];
  edges
    .filter(edge => edge.from === nodeId)
    .forEach(edge => ids.push(...collectDescendants(edge.to, edges)));
  return ids;
};

const isBlank = value =>
  value === undefined || value === null || `${value}`.trim() === '';

const findPipeline = (context, id) =>
  context.pipelines.find(pipeline => pipeline.id === Number(id));

const hasStages = (pipeline, ids) =>
  ids.every(id => pipeline?.stages?.some(stage => stage.id === Number(id)));

// Confere se pipeline e etapas escolhidas ainda existem.
const pipelineReferenceKeys = (context, pipelineId, stageIds, keys) => {
  if (!pipelineId) return [];
  const pipeline = findPipeline(context, pipelineId);
  if (!pipeline) return [keys.pipeline];
  return stageIds.length && !hasStages(pipeline, stageIds) ? [keys.stage] : [];
};

const valueKeys = (field, value, keys) => {
  if (hasFixedOptions(field)) {
    const known = field.options.some(option => option.value === `${value}`);
    return known ? [] : [keys.missing];
  }
  if (field.type === 'boolean') {
    return ['true', 'false'].includes(`${value}`) ? [] : [keys.invalid];
  }
  return valueMatchesType(field.type, value) ? [] : [keys.invalid];
};

const ruleReferenceKeys = (rule, index, groups) => {
  if (!rule.attribute) return [];
  const field = findField(groups, rule);
  if (!field) return [`ATTRIBUTE_MISSING@${index}`];
  if (!operatorNeedsValue(rule.operator) || isBlank(rule.value)) return [];
  return valueKeys(field, rule.value, {
    missing: `VALUE_MISSING@${index}`,
    invalid: `VALUE_INVALID@${index}`,
  });
};

const rowReferenceKeys = (row, index, groups) => {
  if (!row.field) return [];
  const field = findField(groups, decodeField(row.field));
  if (!field) return [`FIELD_MISSING@${index}`];
  // Uma variável ({{ deal.value }}) só vira valor quando a automação roda.
  if (isBlank(row.value) || /\{\{.+?\}\}/.test(row.value)) return [];
  return valueKeys(field, row.value, {
    missing: `FIELD_VALUE_MISSING@${index}`,
    invalid: `FIELD_VALUE_INVALID@${index}`,
  });
};

const metaReferenceKeys = (data, groups) =>
  Object.values(data.meta_fields || {}).some(
    id => id && !isMetaFixed(id) && !findField(groups, decodeField(id))
  )
    ? ['META_FIELD_MISSING']
    : [];

const referenceKeys = ({ type, data }, context, groups) => {
  if (type === 'trigger') {
    if (!isDealEvent(data.event)) return [];
    const stageIds = data.any_stage === false ? data.stage_ids || [] : [];
    return pipelineReferenceKeys(context, data.pipeline_id, stageIds, {
      pipeline: 'PIPELINE_MISSING',
      stage: 'STAGES_MISSING',
    });
  }
  if (type === 'condition') {
    return (data.rules || []).flatMap((rule, index) =>
      ruleReferenceKeys(rule, index, groups)
    );
  }
  if (isMetaAction(data.action)) {
    return [
      ...(context.metaConnected ? [] : ['META_NOT_CONNECTED']),
      ...metaReferenceKeys(data, groups),
    ];
  }
  if (!isRecordAction(data.action)) return [];
  return [
    ...(data.action === 'create_deal'
      ? pipelineReferenceKeys(
          context,
          data.pipeline_id,
          data.stage_id ? [data.stage_id] : [],
          { pipeline: 'PIPELINE_MISSING', stage: 'STAGE_MISSING' }
        )
      : []),
    ...(data.fields || []).flatMap((row, index) =>
      rowReferenceKeys(row, index, groups)
    ),
  ];
};

const validateRequired = ({ type, data }, event) => {
  if (type === 'trigger') {
    if (!data.event) return ['EVENT_REQUIRED'];
    if (!isDealEvent(data.event)) return [];
    const keys = [];
    if (!data.pipeline_id) keys.push('PIPELINE_REQUIRED');
    if (data.any_stage === false && !data.stage_ids?.length) {
      keys.push('STAGES_REQUIRED');
    }
    return keys;
  }
  if (type === 'condition') {
    return (data.rules || []).flatMap((rule, index) => {
      const keys = [];
      if (!rule.attribute) keys.push(`ATTRIBUTE_REQUIRED@${index}`);
      if (!rule.operator) keys.push(`OPERATOR_REQUIRED@${index}`);
      if (operatorNeedsValue(rule.operator) && isBlank(rule.value)) {
        keys.push(`VALUE_REQUIRED@${index}`);
      }
      return keys;
    });
  }
  if (!data.action) return ['ACTION_REQUIRED'];
  if (isDealEvent(event) && isConversationAction(data.action)) {
    return ['ACTION_NEEDS_CONVERSATION'];
  }
  if (
    isRecordAction(data.action) ||
    isPixelAction(data.action) ||
    isMetaAction(data.action) ||
    isWaitAction(data.action)
  ) {
    return validateActionBody(data);
  }
  return actionNeedsValue(data.action) && isBlank(data.value)
    ? ['ACTION_VALUE_REQUIRED']
    : [];
};

// Retorna [{ nodeId, keys }] com os erros de cada nó incompleto.
// Com `context` (pipelines, agentes e atributos carregados), também confere se
// o que o fluxo referencia ainda existe e se os valores respeitam o tipo.
export const validateFlow = (nodes, context = null) => {
  const event = nodes.find(node => node.type === 'trigger')?.data.event;
  const groups = context ? context.groupsFor(event) : null;
  return nodes
    .map(node => ({
      nodeId: node.id,
      keys: [
        ...validateRequired(node, event),
        ...(context ? referenceKeys(node, context, groups) : []),
      ],
    }))
    .filter(({ keys }) => keys.length);
};
